import { TvdDrillDownGrouping, TVDLocationDetails, TVDLocationDetailsRowData, TvdLocationServicesDetails, TVDProductDetails, TVDProductDetailsRow } from './../types/tvd-types';
import { tvdData, TVDRateDetailsResponse } from 'src/app/features/total-value-delivery/types/tvd-types';
import { Injectable } from '@angular/core';
import { eroiIds, TvdMetricData, componentConfigAndMetricsIdsMap, PestMetricIds, InsitutionalMetricIds, ModifiedFormData, InteractionContext, TVDLocationServicesDetailsRowData } from '../types/tvd-types';
import { TVDService } from '../services/tvd.service';
import { FormGroup } from '@angular/forms';
import { TVDModel } from '../types/tvd-model';
import { EroiMetricComponent } from '../components/eroi-metric/eroi-metric.component';
import { MetricNamesMap } from '../types/tvd-types';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ParseNumberPipe } from 'src/app/core/pipes/parse-number.pipe';
import { FlatMetricCostAssumptions, MetricCostAssumptions } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { UomOption } from 'src/app/core/services/currency-uom.service';

@Injectable({
  providedIn: 'root'
})
export class TvdCostAssumptionsService {
  private parseNumberPipe = new ParseNumberPipe();

  formControlNames: Map<string, string> = new Map([
    ['water', 'water'],
    ['energy', 'energy'],
    [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE, 'generalcontractorcostavoidance'],
    [eroiIds.PESTPREVENTIONFINDINGS, 'preventionfindings'],
    [eroiIds.FURNITUREPRESERVATIONASSETS, 'furniturepreservationassets'],
    [eroiIds.ROOMNIGHTSSAVED,'roomprice']
  ]);
  configAndMetricsIdMaps = componentConfigAndMetricsIdsMap;

  private interactionContext: InteractionContext = {
    type: '',
    metricName: ''
  };

  // Tracks the currency code that is the basis for the current cost assumptions values.
  // This is used to avoid multiple unnecessary currency conversions in tvd-sidenavbar component.
  private costAssumptionsCurrencyCode$ : BehaviorSubject<string> = new BehaviorSubject<string>('');
  costAssumptionsCurrencyCodeObservable: Observable<string> = this.costAssumptionsCurrencyCode$.asObservable();
   

  // Tracks the unit of measure system that is the basis for the current cost assumptions values.
  // This is used to avoid multiple unnecessary conversions in tvd-sidenavbar component.
  private costAssumptionsUnitOfMeasureSystem$ : BehaviorSubject<UomOption> = new BehaviorSubject<UomOption>('Imperial');
  costAssumptionsUnitOfMeasureSystemObservable: Observable<UomOption> = this.costAssumptionsUnitOfMeasureSystem$.asObservable();
   

  // Tracks the number of times the cost assumptions form values have been updated.
  // Other components can subscribe to this observable to be notified when the cost assumptions form values are updated.
  private costAssumptionsFormValuesUpdated$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  costAssumptionsUpdatedFormValuesObservable: Observable<number> = this.costAssumptionsFormValuesUpdated$.asObservable();

  public notifyCostAssumptionsFormValuesUpdated(): void {
    this.costAssumptionsFormValuesUpdated$.next(this.costAssumptionsFormValuesUpdated$.getValue() + 1);
  }

  constructor(private _tvdService: TVDService) { }

  public setCostAssumptionsCurrencyCode(value: string): void {
    this.costAssumptionsCurrencyCode$.next(value);
  }
  public getCostAssumptionsCurrencyCode(): string {
    return this.costAssumptionsCurrencyCode$.getValue();
  }

  public setCostAssumptionsUnitOfMeasureSystem(value: UomOption): void {
    this.costAssumptionsUnitOfMeasureSystem$.next(value);
  }
  public getCostAssumptionsUnitOfMeasureSystem(): UomOption {
    return this.costAssumptionsUnitOfMeasureSystem$.getValue();
  }

  /**
   * @deprecated This method is deprecated and will be removed in future releases. Please use `TVDService.refreshTVDDataWithAssumptions` method for updating TVD data. For custom tiles, use `TVDService.recalculateTotalsWithCustomTiles`.
   */
  async updateTotalTVDData(tvdData: TVDModel | undefined, isPest: boolean): Promise<void> {

    const metricIds = isPest ? PestMetricIds : InsitutionalMetricIds;
    const savedFormData: ModifiedFormData = this._tvdService.getFormData();
    //Number(savedFormData['water']) > 0
    let overAllTVDValue = this.getFormattedNumber(this._tvdService.getMonetaryContributionValue(eroiIds.TOTALVALUEDELIVERED));

    // Get custom tiles cost
    const customTiles = await firstValueFrom(this._tvdService.customTilesObservable);
    const tvdCustomCostTotal = customTiles
      ? Array.from(customTiles.values()).reduce((sum, current) => sum + parseFloat(current.cost.replace(/,/g, '')), 0)
      : 0;

    overAllTVDValue += tvdCustomCostTotal;

    let originalTVDValue = 0;
    let modifiedTVDValue = 0;

    metricIds.forEach((metricId) => {
      const pestComponentData = this._tvdService.getCostAssumptionsComponentData(metricId)?.data;
      const modifiedMonetaryContribution = this.getFormattedNumber(pestComponentData?.modifiedMonetaryContribution ?? '0');
      originalTVDValue +=  modifiedMonetaryContribution;
      const configIds = this.configAndMetricsIdMaps.get(metricId);
      const monetaryContribution = this.getFormattedNumber(pestComponentData?.monetaryContribution);
      let isSkip = false;
      configIds?.forEach((configId) => {
        if (Number(savedFormData[configId]) > 0 && !isSkip) {
          modifiedTVDValue += monetaryContribution;
          //Added because we have 2 diffrent metrics for productivity.
          //Only once need to add the value to the modifiedTVDValue.
          if (metricId === eroiIds.PRODUCTIVITY || metricId === eroiIds.ROOMNIGHTSSAVED ||
            metricId === eroiIds.SETUPSERVICES)
            isSkip = true;
        }
      });
    });
    const modifiedTotalTVDValue = overAllTVDValue - originalTVDValue + modifiedTVDValue;

    const tvdResponse = tvdData?.
      tvdMetricSelections.find(x => x.id == this._tvdService.getGroupId())?.
      summMetrics.find(x => x.id === eroiIds.TOTALVALUEDELIVERED) ?? {
        id: '',
        '$type': 'TvdCostMetric',
        brand: undefined,
        sortSequenceNumber: 0,
        monetaryContribution: '0',
        valueContribution: '0',
        modifiedMonetaryContribution: '0'
      };

    //update the total value delivered
    // this.updateTotalValueDelivered(tvdResponse, modifiedTotalTVDValue, overAllTVDValue);
  }

  // Helper method to calculate estimated metric dollars
  public calculateEstimatedMetricDollars(modifierValue: number, metricQuantity: number, isWaterDrilldown: boolean): number {
    return isWaterDrilldown
      ? modifierValue * metricQuantity / 1000
      : modifierValue * metricQuantity;
  }

  public calculatePestEstimatedMetricDollars(modifierValue: number, metricQuantity: number, isFurnitureAssets: boolean): number {
    let furnitureAssetsMultiplier = this._tvdService.getDefaultRate("furniturepreservationassetsmultiplier", null) as number;
    return isFurnitureAssets
      ? modifierValue * metricQuantity * furnitureAssetsMultiplier
      : modifierValue * metricQuantity;
  }

  // Helper method to calculate modified average TVD metric quantity
  public calculateModifiedAvgTVDMetricQuantity(modifierValue: number, avgTVDMetricQuantity: string, isWaterDrilldown: boolean): string {
    return (
      isWaterDrilldown
        ? modifierValue * Number(avgTVDMetricQuantity) / 1000
        : modifierValue * Number(avgTVDMetricQuantity)
    ).toString();
  }

  // Helper method to calculate modified average TVD metric quantity for pest
  public calculatePestModifiedAvgTVDMetricQuantity(modifierValue: number, avgTVDMetricQuantity: string, isFurnitureAssets: boolean): string {
    let furnitureAssetsMultiplier = this._tvdService.getDefaultRate("furniturepreservationassetsmultiplier", null) as number;
    return (
      isFurnitureAssets
        ? modifierValue * Number(avgTVDMetricQuantity) * furnitureAssetsMultiplier
        : modifierValue * Number(avgTVDMetricQuantity)
    ).toString();
  }

  // Method for two modifiers
  public calculatePestEstimatedMetricDollarsWithTwoModifiers(modifierValue1: number, modifierValue2: number, metricQuantity: number, isFurnitureAssets: boolean): number {
    let furnitureAssetsMultiplier = this._tvdService.getDefaultRate("furniturepreservationassetsmultiplier", null) as number;
    return isFurnitureAssets
      ? modifierValue1 * modifierValue2 * metricQuantity * furnitureAssetsMultiplier
      : modifierValue1 * modifierValue2 * metricQuantity;
  }

  public calculatePestModifiedAvgTVDMetricQuantityWithTwoModifiers(modifierValue1: number, modifierValue2: number, avgTVDMetricQuantity: string, isFurnitureAssets: boolean): string {
    let furnitureAssetsMultiplier = this._tvdService.getDefaultRate("furniturepreservationassetsmultiplier", null) as number;
    return (
      isFurnitureAssets
        ? modifierValue1 * modifierValue2 * Number(avgTVDMetricQuantity) * furnitureAssetsMultiplier
        : modifierValue1 * modifierValue2 * Number(avgTVDMetricQuantity)
    ).toString();
  }

  // TODO : This method is called in a few places to format the string to a number. Consider refactoring to use ParseNumberPipe directly where needed.
  public getFormattedNumber(value: any): number {
    return this.parseNumberPipe.transform(value)
  }

  /**
   * Updates the component data based on the provided form control name.
   *
   * @param componentData - The component data to be updated. It can be of type `EroiMetricComponent` or `undefined`.
   * @param formControlName - The name of the form control whose value will be used to update the component data.
   *
   * This method performs the following operations:
   * - Checks if `componentData` and `componentData.data` are defined.
   * - Retrieves the value from the form control specified by `formControlName` and converts it to a number.
   * - If the value is greater than 0, updates `componentData.data.modifiedMonetaryContribution` with `componentData.data.monetaryContribution`.
   * - Calculates the `valueContribution` by removing commas from `componentData.data.valueContribution` and converting it to a number.
   * - Updates `componentData.data.monetaryContribution` by multiplying `valueContribution` with the form control value and converting the result to a localized string.
   */
  updateComponentData(componentData: EroiMetricComponent | undefined, formControlName: string, modifierForm: FormGroup, factors?: number[], furnitureAssetsMultiplier?: number): void {

    if (!componentData?.data)
      return;

    const mappedFormControlName = this.getFormControlName(formControlName);
    let modifierValue = Number(modifierForm.value[mappedFormControlName]);

    // If no room price is set, assign modifierValue to the value of number of room nights
    if (mappedFormControlName == 'roomprice' && modifierValue <= 0) {
      modifierValue = Number(modifierForm.value['numberofroomnights']); // Default rate for room price
    }
    if (modifierValue <= 0 || isNaN(modifierValue))
      return;


    // Calculate using either multiple factors or single modifier
    const monetaryContribution = this._tvdService.getMonetaryContributionValue(formControlName);
    componentData.data.modifiedMonetaryContribution = monetaryContribution;

    const valueContribution = this.getFormattedNumber(componentData.data.valueContribution);
    let calculatedValue: number;

    // Calculate using either multiple factors or single modifier
    if (factors && factors.length > 0) {
      calculatedValue = this.calculateWithFactors(valueContribution, factors);

      // Update valueContribution(nights count) when the "# of Days" in Room Nights Saved metric uses cost adjustment factor - US536518
      //factors[1] corresponds to the value of "# of Days" modified/adjusted value - Default is 1 day/s
      if(mappedFormControlName == 'roomprice' && factors[1] > 0) {
        componentData.data.modifiedValueContribution = (valueContribution * factors[1]).toLocaleString();
      }
    } else {
      const secondMulitplier = furnitureAssetsMultiplier ? furnitureAssetsMultiplier : 1;
      const calculationFactor = formControlName === 'water' ? 1 / 1000 : 1;
      calculatedValue = valueContribution * modifierValue * calculationFactor * secondMulitplier;
    }
    componentData.data.monetaryContribution = Math.round(calculatedValue).toLocaleString();
  }

  /**
   * Updates the component data for two-factor metrics based on provided factors and monetary contribution.
   * Ensures valid inputs and calculates the monetary contribution for the component.
   */
  updateComponentDataForTwoFactorMetrics(componentData: EroiMetricComponent | undefined, formControlName: string, modifierForm: FormGroup, twoFactorControlIds: string[]) {
    if (!componentData?.data)
      return;

    const [valueFactor, countFactor] = this.getFactors(twoFactorControlIds, modifierForm);

    if (valueFactor <= 0 || isNaN(valueFactor) && countFactor <= 0 || isNaN(countFactor))
      return;

    this.updateComponentData(componentData, formControlName, modifierForm, [valueFactor, countFactor]);
  }

  private calculateWithFactors(value: number, factors: number[]): number {
    return factors.reduce((result, factor) => result * factor, value);
  }

  private getFormControlName(id: string): string {
    return this.formControlNames.get(id) ?? id;
  }

  private getFactors(twoFactorControlIds: string[], modifierForm: FormGroup): [number, number] {
    // TODO : Change formControlNames to a Map<string, list<string>> if more than 2 factors are needed
    let factorValue = twoFactorControlIds[0] == 'roomprice' ? this._tvdService.getDefaultRate("roomprice", null) : 1; // Default rate for room price
    const valueFactor = Number(modifierForm.value[twoFactorControlIds[0]]) || Number(factorValue); // Assign 1 if value is 0 or NaN
    const countFactor = Number(modifierForm.value[twoFactorControlIds[1]]) || 1; // Assign 1 if value is 0 or NaN
    return [valueFactor, countFactor];
  }

  private updateTotalValueDelivered(metricsData: TvdMetricData | undefined, modifiedTotalValueDelivered: number, overAllTVDValue: number): void {
    if (metricsData) {
      metricsData.monetaryContribution = modifiedTotalValueDelivered.toLocaleString();
      metricsData.modifiedMonetaryContribution = overAllTVDValue.toLocaleString();

      // Updating the total TVD data, used in the TVD-Overall component
      this._tvdService.updateTotalValueDelivered(metricsData);
    }
  }

  //Setter and Getters for the interaction type context
  setInteractionContext(type: string, metricName: string = ''): void {
    this.interactionContext = { type, metricName };
  }

  getInteractionContext(): InteractionContext {
    return { ...this.interactionContext };
  }

  resetInteractionContext(): void {
    this.interactionContext = { type: '', metricName: '' };
  }

  // Updates metric data in the TVD model and emits the updated data
  updateMetricDataInSubject(metricId: string, tvdData: TVDModel | undefined) {
    let updateMetricsData = this.getMetricData(metricId);
    if (tvdData && updateMetricsData != undefined) {
      const updateMetrics = (metricsData: TvdMetricData | undefined) => {
        if (metricsData) {
          metricsData.monetaryContribution = updateMetricsData?.monetaryContribution;
          metricsData.valueContribution = updateMetricsData?.valueContribution ?? '';
          metricsData.modifiedMonetaryContribution = updateMetricsData?.modifiedMonetaryContribution;
          metricsData.modifiedValueContribution = updateMetricsData?.modifiedValueContribution ?? '';
        }
      };

      const tvdResponse = tvdData.tvdResponse
        .flatMap(group => group.indvMetrics)
        .find(metric => metric.id === metricId);
      updateMetrics(tvdResponse);

      const tvdMetricGroups = tvdData.tvdMetricSelections
        .flatMap(group => group.indvMetrics)
        .find(metric => metric.id === metricId);
      updateMetrics(tvdMetricGroups);

      // Emit the updated data
      this._tvdService.updateTvdData(tvdData);
    }
  }

  /**
   * Retrieves the metric data associated with the given metric ID.
   * Returns `undefined` if no matching data is found.
   *
   * @param metricId - The unique identifier for the metric.
   * @returns The data corresponding to the metric ID or `undefined`.
  */
  getMetricData(metricId: string) {
    const dataMap: { [key: string]: any } = {
      [eroiIds.ENERGY]: this._tvdService.getCostAssumptionsComponentData(eroiIds.ENERGY)?.data,
      [eroiIds.WATER]: this._tvdService.getCostAssumptionsComponentData(eroiIds.WATER)?.data,
      [eroiIds.PRODUCTIVITY]: this._tvdService.getCostAssumptionsComponentData(eroiIds.PRODUCTIVITY)?.data,
      [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE]: this._tvdService.getCostAssumptionsComponentData(eroiIds.GENERALCONTRACTORCOSTAVOIDANCE)?.data,
      [eroiIds.PESTPREVENTIONFINDINGS]: this._tvdService.getCostAssumptionsComponentData(eroiIds.PESTPREVENTIONFINDINGS)?.data,
      [eroiIds.FURNITUREPRESERVATIONASSETS]: this._tvdService.getCostAssumptionsComponentData(eroiIds.FURNITUREPRESERVATIONASSETS)?.data,
      [eroiIds.ROOMNIGHTSSAVED]: this._tvdService.getCostAssumptionsComponentData(eroiIds.ROOMNIGHTSSAVED)?.data,
      [eroiIds.SETUPSERVICES]: this._tvdService.getCostAssumptionsComponentData(eroiIds.SETUPSERVICES)?.data,
      [eroiIds.NOCHARGEADDITIONALSERVICES]: this._tvdService.getCostAssumptionsComponentData(eroiIds.NOCHARGEADDITIONALSERVICES)?.data,
      [eroiIds.FURNITUREPRESERVATIONENERGY]: this._tvdService.getCostAssumptionsComponentData(eroiIds.FURNITUREPRESERVATIONENERGY)?.data
    };

    return dataMap[metricId] || undefined;
  }

  /**
    * Builds a dictionary of metrics properties containing default and saved values
    * for rates that have been modified by the user to add it to Mixpanel event
  **/
  buildMetricsProperties(tvdRateDetailsResponse: TVDRateDetailsResponse | undefined): Record<string, string | number> {
    const metricsProperties: Record<string, string | number> = {};

    // Get default rates from TVD rate details response
    const defaultRates = tvdRateDetailsResponse?.tvdRateDetails || [];
    if (!defaultRates?.length) return metricsProperties;

    // Get user-modified values from form data
    const formData = this._tvdService.getFormData();
    if (!formData) return metricsProperties;

    // Process each rate and build properties for modified values only
    for (const rate of defaultRates) {
      const modifiedValue = formData[rate.id];

      // Skip if no modified value exists or if it's zero/negative
      // Only include rates that have been changed by the user
      if (!modifiedValue || this.getFormattedNumber(modifiedValue) <= 0) {
        continue;
      }

      const displayName = MetricNamesMap[rate.id] || rate.id;

      // Direct assignment (avoiding string conversion if rate is already string)
      metricsProperties[`${displayName} - Default Value`] = String(rate.rate);
      metricsProperties[`${displayName} - Saved Value`] = modifiedValue;
    }

    return metricsProperties;
  }

  /**
  * Checks if component data has valid monetary and value contributions
  */
  hasValidContribution(data: tvdData | undefined): boolean {
    if (!data) return false;

    const hasMonetaryContribution = data.monetaryContribution !== undefined &&
      data.monetaryContribution !== '' &&
      data.monetaryContribution !== '0';

    const hasValueContribution = data.valueContribution !== undefined &&
      data.valueContribution !== '' &&
      data.valueContribution !== '0';

    return hasMonetaryContribution && hasValueContribution;
  }

  /**
   * Updates metric data in the TVD model
   * @param metricId - The metric identifier to update
   * @param tvdData - The TVD model containing the data
   */
  updateMetricDataInTvdModel(metricId: string, tvdData: TVDModel | undefined): void {
    if (!tvdData) return;

    const updateMetricsData = this.getMetricData(metricId);
    if (!updateMetricsData) return;

    const metricsData = tvdData.tvdResponse
      .flatMap(group => group.indvMetrics)
      .find(metric => metric.id === metricId);

    if (metricsData) {
      metricsData.monetaryContribution = updateMetricsData.monetaryContribution;
      metricsData.valueContribution = updateMetricsData.valueContribution ?? '';
      metricsData.modifiedMonetaryContribution = updateMetricsData.modifiedMonetaryContribution;
      metricsData.modifiedValueContribution = updateMetricsData.modifiedValueContribution ?? '';
    }

    // Emit the updated data
    this._tvdService.updateTvdData(tvdData);
  }

  /**
   * Processes and saves metric data for a given metric ID
   * @param metricId - The metric identifier
   * @param tvdData - The TVD model
   * @param isPestDivision - Whether this is for pest division
   */
  processAndSaveMetricData(metricId: string, tvdData: TVDModel | undefined, isPestDivision: boolean = false): void {
    const metricData = this.getMetricData(metricId);
    if (!metricData) return;

    // Check if metric has valid contribution data
    const hasValidData = metricData.monetaryContribution !== '' && metricData.valueContribution !== '0';
    if (!hasValidData) return;

    // Update the metric data in the model
    this.updateMetricDataInTvdModel(metricId, tvdData);

    // Update in subject for reactive updates
    this.updateMetricDataInSubject(metricId, tvdData);
  }

  /**
   * Consolidated method for saving/updating non-pest drill-down TVD metric data
   * Used by both dialog and sidenavbar components
  */
  async saveNonPestDrillDownTVDMetricData(
    drilldownComponentTitle: string | undefined,
    formData: any,
    isDrillDownModal: boolean,
    selectedProgram: string,
    tvdLocationDetailsData?: TVDLocationDetails,
    tvdProductDetailsData?: TVDProductDetails
  ): Promise<void> {

    if (!drilldownComponentTitle || !isDrillDownModal) {
      return;
    }

    const drilldownKey = drilldownComponentTitle.toLowerCase();
    const modifierValue = formData[drilldownKey];
    const isWaterDrilldown = drilldownKey === eroiIds.WATER;

    // Check if productivity metrics are set and have data
    if (drilldownKey === eroiIds.PRODUCTIVITY && this.hasValidProductivityFormData(formData)) {
      await this.handleProductivityDrilldownUpdate(formData, selectedProgram, tvdLocationDetailsData, tvdProductDetailsData);
    } else if (modifierValue > 0) {
      // Handle non-productivity metrics (water, energy)
      this.handleNonProductivityDrilldownUpdate(modifierValue, isWaterDrilldown, tvdLocationDetailsData, tvdProductDetailsData);
    }
  }

  /**
   * Checks if productivity form data has valid values
   */
  private hasValidProductivityFormData(formData: any): boolean {
    return formData['hospitality'] > 0 || formData['foodservice'] > 0 || formData['productivity'] > 0;
  }

  /**
   * Handles productivity drill-down updates with API calls
   */
  private async handleProductivityDrilldownUpdate(
    formData: any,
    selectedProgram: string,
    tvdLocationDetailsData?: TVDLocationDetails,
    tvdProductDetailsData?: TVDProductDetails
  ): Promise<void> {

    const convertedMetrics = this.extractProductivityMetrics(formData);

    try {
      // Create promises for both product and location API calls
      const [productData, locationData] = await Promise.all([
        this._tvdService.getTVDDetailsData(TvdDrillDownGrouping.Product, eroiIds.PRODUCTIVITY, selectedProgram, convertedMetrics),
        this._tvdService.getTVDDetailsData(TvdDrillDownGrouping.Location, eroiIds.PRODUCTIVITY, selectedProgram, convertedMetrics)
      ]);

      // Update product rows
      if (tvdProductDetailsData && productData) {
        this.updateProductRowsWithApiData(tvdProductDetailsData, productData);
        this._tvdService.setTvdProductDetailsData(tvdProductDetailsData);
      }

      // Update location rows
      if (tvdLocationDetailsData && locationData) {
        this.updateLocationRowsWithApiData(tvdLocationDetailsData, locationData);
        this._tvdService.setTvdLocationDetailsData(tvdLocationDetailsData);
      }

    } catch (error) {
      console.error(`Error fetching TVD services details data after customizations for ${eroiIds.PRODUCTIVITY}:`, error);
    }
  }

  /**
   * Handles non-productivity metrics (water, energy) with local calculations
   */
  private handleNonProductivityDrilldownUpdate(
    modifierValue: number,
    isWaterDrilldown: boolean,
    tvdLocationDetailsData?: TVDLocationDetails,
    tvdProductDetailsData?: TVDProductDetails
  ): void {

    // Update location rows
    if (tvdLocationDetailsData?.locationRows) {
      tvdLocationDetailsData.locationRows.forEach((location) => {
        location.estimatedMetricDollars = this.calculateEstimatedMetricDollars(
          modifierValue,
          location.estimatedMetricQuantity,
          isWaterDrilldown
        );
      });

      // Update average TVD metric quantity for locations
      if (tvdLocationDetailsData.avgTVDMetricQuantity) {
        tvdLocationDetailsData.modifiedAvgTVDMetricQuantity = this.calculateModifiedAvgTVDMetricQuantity(
          modifierValue,
          tvdLocationDetailsData.avgTVDMetricQuantity,
          isWaterDrilldown
        );
      }

      this._tvdService.setTvdLocationDetailsData(tvdLocationDetailsData);
    }

    // Update product rows
    if (tvdProductDetailsData?.productRows) {
      tvdProductDetailsData.productRows.forEach((product) => {
        product.estimatedMetricDollars = this.calculateEstimatedMetricDollars(
          modifierValue,
          product.estimatedMetricQuantity,
          isWaterDrilldown
        );
      });

      // Update average TVD metric quantity for products
      if (tvdProductDetailsData.avgTVDMetricQuantity) {
        tvdProductDetailsData.modifiedAvgTVDMetricQuantity = this.calculateModifiedAvgTVDMetricQuantity(
          modifierValue,
          tvdProductDetailsData.avgTVDMetricQuantity,
          isWaterDrilldown
        );
      }

      this._tvdService.setTvdProductDetailsData(tvdProductDetailsData);
    }
  }

  /**
   * Extracts productivity metrics from form data
   */
  private extractProductivityMetrics(formData: any): MetricCostAssumptions {
    const convertedMetrics: Record<string, number> = {};
    const productivityMetricIds = componentConfigAndMetricsIdsMap.get(eroiIds.PRODUCTIVITY) || [];

    productivityMetricIds.forEach(field => {
      const value = parseFloat(formData[field]);
      if (!isNaN(value) && value > 0) {
        convertedMetrics[field] = value;
      }
    });

    const metricCostAssumptions: FlatMetricCostAssumptions = new FlatMetricCostAssumptions();
    metricCostAssumptions.rates = convertedMetrics;
    return metricCostAssumptions;
  }

  /**
   * Updates product rows with API response data
   */
  private updateProductRowsWithApiData(
    tvdProductDetailsData: TVDProductDetails,
    productData: any
  ): void {
    productData.productRows.forEach((product: TVDProductDetailsRow) => {
      product.estimatedMetricDollars = product.modifiedEstimatedMetricDollars;
    });

    tvdProductDetailsData.productRows = productData.productRows;

    if (productData.avgTVDMetricQuantity !== undefined) {
      tvdProductDetailsData.modifiedAvgTVDMetricQuantity = productData.avgTVDMetricQuantity;
    }
  }

  /**
   * Updates location rows with API response data
   */
  private updateLocationRowsWithApiData(
    tvdLocationDetailsData: TVDLocationDetails,
    locationData: any
  ): void {
    locationData.locationRows.forEach((location: TVDLocationDetailsRowData) => {
      location.estimatedMetricDollars = location.modifiedEstimatedMetricDollars;
    });

    tvdLocationDetailsData.locationRows = locationData.locationRows;

    if (locationData.avgTVDMetricQuantity !== undefined) {
      tvdLocationDetailsData.modifiedAvgTVDMetricQuantity = locationData.avgTVDMetricQuantity;
    }
  }

  /**
   * Common pest drill-down save logic for both dialog and sidenavbar
   * @param drilldownComponentTitle - The component title
   * @param tvdLocationServicesData - Location services data
   * @param formData - Form data from either component
   * @param isDrillDownModal - Whether this is from drill-down modal
  */
  async savePestDrillDownTVDMetricData(
    drilldownComponentTitle: string | undefined,
    tvdLocationServicesData: TvdLocationServicesDetails | undefined,
    formData: any,
    isDrillDownModal: boolean,
    metricsConfig?: any[]
  ): Promise<TvdLocationServicesDetails | undefined> {

    if (!drilldownComponentTitle || !isDrillDownModal || !tvdLocationServicesData) {
      return tvdLocationServicesData;
    }

    const drilldownKey = drilldownComponentTitle.toLowerCase();

    const convertedMetrics = this.getPestPayloadFromFormData(formData, metricsConfig);
    let metricTitle = drilldownKey.split("_").join("-");

    if (drilldownKey === eroiIds.SETUPSERVICES) {
      metricTitle = "set-up-services";
    }

    try {
      const locationServicesData = await this._tvdService.getTVDServicesDetailsData('location', metricTitle, convertedMetrics);

      if (locationServicesData) {
        // Update location rows data
        locationServicesData.locationRows.forEach((location: TVDLocationServicesDetailsRowData) => {
          location.estimatedMetricDollars = location.modifiedEstimatedMetricDollars;
        });

        tvdLocationServicesData.locationRows = locationServicesData.locationRows;

        if (locationServicesData.avgPerServiceCost !== undefined) {
          tvdLocationServicesData.modifiedAvgPerServiceCost = locationServicesData.avgPerServiceCost;
        }

        this._tvdService.setPestTvdLocationDetailsData(locationServicesData);
        return locationServicesData;
      }
    } catch (error) {
      console.error(`Error fetching TVD services details data after customizations for ${drilldownKey}:`, error);
    }

    return tvdLocationServicesData;
  }

  /**
   * Pest payload method that handles both component structures and filtering
   * @param formData - Angular form or plain object from service
   * @param metricsConfig - Metrics configuration for filtering (optional)
  */
  getPestPayloadFromFormData(formData: any, metricsConfig?: any[]): MetricCostAssumptions { // TODO: Replace with MetricCostAssumptions
    const metricCostAssumptions: FlatMetricCostAssumptions = new FlatMetricCostAssumptions();
    metricCostAssumptions.rates = {};

    // If no metricsConfig provided, include all valid numeric values
    let filteredIds: string[] = [];

    if (metricsConfig) {
      filteredIds = metricsConfig.flatMap(metricConfig =>
        metricConfig.metrics
          .filter((metric: any) => metric.isIncludePayload)
          .flatMap((metric: any) => [
            metric.id,
            ...(metric.isTwoFactorsApplicable && metric.twoFactorMetrics
              ? metric.twoFactorMetrics.map((twoFactorMetric: any) => twoFactorMetric.id)
              : [])
          ])
      );
    }

    // Handle Angular form structure (from sidenavbar)
    if (formData.controls) {
      const formControls = formData.controls;
      for (const key in formControls) {
        if (formControls.hasOwnProperty(key)) {
          const control = formControls[key];
          if (control && control.value) {
            const value = parseFloat(control.value);
            if (!isNaN(value) && (filteredIds.length === 0 || filteredIds.includes(key))) {
              metricCostAssumptions.rates[key] = value;
            }
          }
        }
      }
    } else {
      // Handle plain object structure (from dialog's _tvdService.getFormData())
      for (const key in formData) {
        if (formData.hasOwnProperty(key)) {
          const value = parseFloat(formData[key]);
          if (!isNaN(value) && (filteredIds.length === 0 || filteredIds.includes(key))) {
            metricCostAssumptions.rates[key] = value;
          }
        }
      }
    }

    return metricCostAssumptions;
  }

  /**
   * Creates a component data map for service usage - common logic for both components
   * @param isPestDivision - Whether this is pest division
   * @param pestTVDMetricsData - Pest metrics data (if pest division)
   * @param productivityComponent - Productivity component (if non-pest)
   * @param energyComponent - Energy component (if non-pest)
   * @param waterComponent - Water component (if non-pest)
   */
  createComponentDataMap(
    isPestDivision: boolean,
    pestTVDMetricsData?: any,
    productivityComponent?: EroiMetricComponent,
    energyComponent?: EroiMetricComponent,
    waterComponent?: EroiMetricComponent
  ): Map<string, EroiMetricComponent | undefined> {

    const componentMap = new Map<string, EroiMetricComponent | undefined>();

    if (isPestDivision && pestTVDMetricsData) {
      // Map pest components
      componentMap.set(eroiIds.NOCHARGEADDITIONALSERVICES, pestTVDMetricsData.noChargeAdditionalServicesComponent);
      componentMap.set(eroiIds.GENERALCONTRACTORCOSTAVOIDANCE, pestTVDMetricsData.generalContractorCostAvoidanceComponent);
      componentMap.set(eroiIds.SETUPSERVICES, pestTVDMetricsData.setupServicesComponent);
      componentMap.set(eroiIds.PESTPREVENTIONFINDINGS, pestTVDMetricsData.pestPreventionFindingsComponent);
      componentMap.set(eroiIds.ROOMNIGHTSSAVED, pestTVDMetricsData.roomNightsSavedComponent);
      componentMap.set(eroiIds.FURNITUREPRESERVATIONASSETS, pestTVDMetricsData.furniturePreservationAssetsComponent);
      componentMap.set(eroiIds.FURNITUREPRESERVATIONENERGY, pestTVDMetricsData.furniturePreservationEnergyComponent);
    } else {
      // Map non-pest components
      componentMap.set(eroiIds.PRODUCTIVITY, productivityComponent);
      componentMap.set(eroiIds.ENERGY, energyComponent);
      componentMap.set(eroiIds.WATER, waterComponent);
    }

    return componentMap;
  }


  getDefaultRate(tvdRateDetailsResponse: TVDRateDetailsResponse | undefined, metricId: string, isDecimal: boolean = true) {
    let rate = (tvdRateDetailsResponse?.tvdRateDetails?.find(x => x.id === metricId)?.rate ?? 0).toFixed(2);
    return isDecimal ? rate : parseFloat(rate);
  }
}
